<script setup>
import { computed, onMounted, reactive, ref } from "vue"
import { useRoute } from "vue-router"
import Swal from "sweetalert2"
import api from "@/api/axios.js"
import { useAuth } from "@/composables/useAuth"

const route = useRoute()
const { user } = useAuth()

const esAdminORrhh = computed(() => user.value?.role === "admin" || user.value?.role === "rrhh")

const empleado = ref(null)
const documentos = ref([])
const vacaciones = ref(null)
const cargando = ref(true)

const TIPOS_DOCUMENTO = [
  { value: "hoja_vida", label: "Hoja de vida" },
  { value: "cedula", label: "Copia de cédula" },
  { value: "contrato", label: "Contrato laboral" },
  { value: "incapacidad", label: "Incapacidad" },
  { value: "solicitud", label: "Solicitud" },
  { value: "memorando", label: "Memorando" },
  { value: "certificacion", label: "Certificación" },
  { value: "llamado_atencion", label: "Llamado de atención" },
  { value: "otro", label: "Otro" }
]

function labelTipo(tipo) {
  return TIPOS_DOCUMENTO.find((t) => t.value === tipo)?.label || tipo
}

// La cédula llega enmascarada por defecto (últimos 4 dígitos); el número
// completo solo se pide bajo demanda y queda auditado en el backend.
const cedulaRevelada = ref(null)

async function revelarCedula() {
  try {
    const { data } = await api.get(`/rrhh/empleados/${empleado.value.id}/cedula`)
    cedulaRevelada.value = data.cedula
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo ver la cédula completa",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}

async function cargarTodo() {
  cargando.value = true
  cedulaRevelada.value = null
  try {
    let empleadoId = route.params.id

    if (empleadoId) {
      const { data } = await api.get(`/rrhh/empleados/${empleadoId}`)
      empleado.value = data.empleado
    } else {
      const { data } = await api.get("/rrhh/mi-empleado")
      empleado.value = data.empleado
      empleadoId = data.empleado.id
    }

    const [docsRes, vacRes] = await Promise.all([
      api.get(`/rrhh/empleados/${empleadoId}/documentos`),
      api.get(`/rrhh/empleados/${empleadoId}/vacaciones`)
    ])
    documentos.value = docsRes.data.documentos
    vacaciones.value = vacRes.data
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar el expediente",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    cargando.value = false
  }
}

onMounted(cargarTodo)

function formatDate(value) {
  if (!value) return "—"
  return new Date(value).toLocaleDateString("es-CO", { timeZone: "UTC", year: "numeric", month: "2-digit", day: "2-digit" })
}

function formatDateTime(value) {
  if (!value) return ""
  return new Date(value).toLocaleString("es-CO")
}

// ─── Subida de documentos ────────────────────────────────────────────────
const subiendo = ref(false)
const nuevoDoc = reactive({ tipo: "hoja_vida", descripcion: "", archivo: null })
const inputArchivo = ref(null)

function onArchivoSeleccionado(e) {
  nuevoDoc.archivo = e.target.files[0] || null
}

async function subirDocumento() {
  if (!nuevoDoc.archivo) {
    Swal.fire({ icon: "warning", title: "Selecciona un archivo" })
    return
  }

  subiendo.value = true
  try {
    const formData = new FormData()
    formData.append("archivo", nuevoDoc.archivo)
    formData.append("tipo", nuevoDoc.tipo)
    formData.append("descripcion", nuevoDoc.descripcion)

    await api.post(`/rrhh/empleados/${empleado.value.id}/documentos`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    })

    nuevoDoc.descripcion = ""
    nuevoDoc.archivo = null
    if (inputArchivo.value) inputArchivo.value.value = ""

    await cargarTodo()

    Swal.fire({ icon: "success", title: "Documento subido", timer: 1600, showConfirmButton: false })
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo subir el documento",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    subiendo.value = false
  }
}

async function eliminarDocumento(doc) {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: "¿Eliminar documento?",
    text: `Se eliminará "${doc.nombreArchivo}" del expediente.`,
    showCancelButton: true,
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#f5365c"
  })
  if (!isConfirmed) return

  try {
    await api.delete(`/rrhh/documentos/${doc.id}`)
    await cargarTodo()
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo eliminar",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}
</script>

<template>
  <div class="container-fluid py-4">
    <div v-if="cargando" class="text-center text-muted py-5">
      <div class="spinner-border spinner-border-sm me-2" role="status"></div>
      Cargando expediente...
    </div>

    <template v-else-if="empleado">
      <div class="row g-4">
        <div class="col-lg-6">
          <div class="card shadow-sm h-100">
            <div class="card-header">
              <h5 class="mb-0"><i class="ni ni-single-02 text-primary me-2"></i>{{ empleado.nombres }} {{ empleado.apellidos }}</h5>
            </div>
            <div class="card-body">
              <dl class="row mb-0">
                <dt class="col-sm-5">Cédula</dt>
                <dd class="col-sm-7">
                  {{ cedulaRevelada ?? (empleado.cedula || "—") }}
                  <button
                    v-if="empleado.cedula && cedulaRevelada === null"
                    type="button"
                    class="btn btn-sm btn-link p-0 ms-1"
                    title="Ver cédula completa"
                    @click="revelarCedula"
                  >
                    <i class="bi bi-eye"></i>
                  </button>
                </dd>
                <dt class="col-sm-5">Fecha de nacimiento</dt>
                <dd class="col-sm-7">{{ formatDate(empleado.fechaNacimiento) }}</dd>
                <dt class="col-sm-5">Teléfono</dt>
                <dd class="col-sm-7">{{ empleado.telefono || "—" }}</dd>
                <dt class="col-sm-5">Dirección</dt>
                <dd class="col-sm-7">{{ empleado.direccion || "—" }}</dd>
                <dt class="col-sm-5">Email personal</dt>
                <dd class="col-sm-7">{{ empleado.emailPersonal || "—" }}</dd>
                <dt class="col-sm-5">Cargo</dt>
                <dd class="col-sm-7">{{ empleado.cargoNombre || empleado.cargo || "—" }}</dd>
                <dt class="col-sm-5">Área</dt>
                <dd class="col-sm-7">{{ empleado.areaNombre || empleado.area || "—" }}</dd>
                <dt class="col-sm-5">Fecha de ingreso</dt>
                <dd class="col-sm-7">{{ formatDate(empleado.fechaIngreso) }}</dd>
                <dt class="col-sm-5">Tipo de contrato</dt>
                <dd class="col-sm-7">{{ empleado.tipoContratoNombre || empleado.tipoContrato || "—" }}</dd>
                <dt class="col-sm-5">Estado</dt>
                <dd class="col-sm-7">
                  <span class="badge" :class="empleado.estado === 'activo' ? 'bg-success' : 'bg-secondary'">
                    {{ empleado.estado }}
                  </span>
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div class="col-lg-6">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0"><i class="ni ni-calendar-grid-58 text-success me-2"></i>Vacaciones</h6>
            </div>
            <div class="card-body">
              <div v-if="vacaciones" class="row text-center">
                <div class="col-3">
                  <p class="text-sm text-muted mb-1">Causados</p>
                  <h5 class="mb-0">{{ vacaciones.causados }}</h5>
                </div>
                <div class="col-3">
                  <p class="text-sm text-muted mb-1">Disfrutados</p>
                  <h5 class="mb-0">{{ vacaciones.disfrutados }}</h5>
                </div>
                <div class="col-3">
                  <p class="text-sm text-muted mb-1">Adelantadas</p>
                  <h5 class="mb-0">{{ vacaciones.adelantadas }}</h5>
                </div>
                <div class="col-3">
                  <p class="text-sm text-muted mb-1">Saldo</p>
                  <h5 class="mb-0" :class="vacaciones.saldo < 0 ? 'text-danger' : 'text-success'">{{ vacaciones.saldo }}</h5>
                </div>
              </div>
            </div>
          </div>

          <div v-if="esAdminORrhh" class="card shadow-sm mt-4">
            <div class="card-header">
              <h6 class="mb-0"><i class="ni ni-cloud-upload-96 text-info me-2"></i>Subir documento</h6>
            </div>
            <div class="card-body">
              <form class="row g-2" @submit.prevent="subirDocumento">
                <div class="col-md-6">
                  <label class="form-label text-sm mb-1">Tipo</label>
                  <select class="form-select form-select-sm" v-model="nuevoDoc.tipo">
                    <option v-for="t in TIPOS_DOCUMENTO" :key="t.value" :value="t.value">{{ t.label }}</option>
                  </select>
                </div>
                <div class="col-md-6">
                  <label class="form-label text-sm mb-1">Descripción (opcional)</label>
                  <input type="text" class="form-control form-control-sm" v-model="nuevoDoc.descripcion">
                </div>
                <div class="col-md-8">
                  <label class="form-label text-sm mb-1">Archivo (PDF, JPG, PNG, DOC, DOCX)</label>
                  <input ref="inputArchivo" type="file" class="form-control form-control-sm" @change="onArchivoSeleccionado"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx">
                </div>
                <div class="col-md-4 d-flex align-items-end">
                  <button type="submit" class="btn btn-primary btn-sm w-100 mb-0" :disabled="subiendo">
                    {{ subiendo ? "Subiendo..." : "Subir" }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div class="card shadow-sm mt-4">
        <div class="card-header">
          <h6 class="mb-0"><i class="ni ni-folder-17 text-warning me-2"></i>Documentos del expediente</h6>
        </div>
        <div class="card-body">
          <div v-if="documentos.length === 0" class="text-center text-muted py-4">
            Aún no hay documentos cargados.
          </div>
          <div v-else class="table-responsive">
            <table class="table table-sm table-striped align-middle">
              <thead class="table-dark">
                <tr>
                  <th>Tipo</th>
                  <th>Archivo</th>
                  <th>Descripción</th>
                  <th>Subido</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="doc in documentos" :key="doc.id">
                  <td><span class="badge bg-light text-dark">{{ labelTipo(doc.tipo) }}</span></td>
                  <td>
                    <a :href="doc.driveUrl" target="_blank" rel="noopener">
                      <i class="bi bi-file-earmark-text me-1"></i>{{ doc.nombreArchivo }}
                    </a>
                  </td>
                  <td>{{ doc.descripcion || "—" }}</td>
                  <td>{{ formatDateTime(doc.createdAt) }}</td>
                  <td>
                    <button v-if="esAdminORrhh" type="button" class="btn btn-sm btn-outline-danger" title="Eliminar" @click="eliminarDocumento(doc)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
